import type { ChatMessage } from '@multimod/shared';
import { PlatformBadge } from './PlatformBadge.js';
import { MsgAvatar } from '@/features/panel/components/MsgAvatar';
import { formatHHMM, formatTimeoutRemaining } from '@/features/panel/lib/time';
import { MsgActions } from './MsgActions';
import type { ChatFeedStore } from './store';

interface Props {
  msg: ChatMessage;
  store: ChatFeedStore;
  index: number;
  onOpenBan: () => void;
  onOpenDelete: () => void;
  onOpenTimeout: () => void;
}

// #55: alguns displayNames YouTube já começam com '@'. Stripa antes de prefixar.
function handle(displayName: string): string {
  return '@' + displayName.replace(/^@+/, '');
}

export function ChatMessageRow({
  msg,
  store,
  index,
  onOpenBan,
  onOpenDelete,
  onOpenTimeout,
}: Props) {
  const isLate = Date.now() - msg.timestamp > 3_000;
  const isDeleted = store((s) => s.deleted.has(`${msg.platform}:${msg.externalId}`));
  const isAuthorBanned = store((s) =>
    s.bannedUsers.has(`${msg.platform}:${msg.author.externalId}`),
  );
  const unmarkBannedUser = store((s) => s.unmarkBannedUser);
  const timeoutExpiresAt = store((s) =>
    s.timedOutUsers.get(`${msg.platform}:${msg.author.externalId}`),
  );
  const unmarkTimedOutUser = store((s) => s.unmarkTimedOutUser);
  const isAuthorTimedOut = timeoutExpiresAt !== undefined;
  const timeoutSecondsLeft = isAuthorTimedOut
    ? Math.max(0, Math.ceil((timeoutExpiresAt! - Date.now()) / 1000))
    : 0;
  const isSelected = store((s) => s.selectedIndex === index);
  const who = handle(msg.author.displayName);

  return (
    <div
      className={
        'msg-row group grid items-start text-sm hover:bg-bg-hover transition-colors duration-instant' +
        (isDeleted ? ' opacity-40' : '')
      }
      data-platform={msg.platform}
      data-selected={isSelected ? 'true' : undefined}
      style={{ gridTemplateColumns: '26px 1fr auto', gap: '8px', padding: '5px 16px 5px 14px' }}
      role="listitem"
    >
      <MsgAvatar displayName={msg.author.displayName} externalId={msg.author.externalId} />

      {/* #127: nick + tags + texto inline (mesma linha, wrap natural quando longo).
          Cada chunk é inline-flex/inline-block sobre baseline. min-w-0 garante
          que o texto trunca/wrap em vez de empurrar o grid. */}
      <div className="min-w-0 leading-relaxed break-words">
        <PlatformBadge platform={msg.platform} />
        <span
          className={
            'ml-1.5 font-medium align-baseline ' +
            (msg.isBroadcaster ? 'nick-broadcaster text-primary' : 'text-fg')
          }
        >
          {msg.author.displayName}
        </span>
        {msg.isModerator && (
          <span className="ml-1 inline-flex items-center justify-center text-2xs font-semibold uppercase px-1 rounded-sm bg-primary-soft text-primary align-baseline">
            M
          </span>
        )}
        {/* #125: BANNED / TIMED OUT depois do nick, antes do texto (ordem
            "quem → status → o que disse"). Visual de tag (bg suave, sem border
            sólida), mas continua <button> 32×32 pra preservar P15 — atalho de
            limpeza quando mod desbaniu via painel nativo. */}
        {isAuthorBanned && (
          <button
            type="button"
            onClick={() => unmarkBannedUser(msg.platform, msg.author.externalId)}
            aria-label={`Limpar marca de banido em ${who}`}
            title="Clique para limpar a marca (use se desbaniu pelo painel nativo)"
            className="ml-1.5 inline-flex items-center justify-center text-2xs font-semibold uppercase tracking-wide px-1.5 min-w-8 h-8 rounded bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors duration-fast cursor-pointer align-middle"
          >
            banned
          </button>
        )}
        {!isAuthorBanned && isAuthorTimedOut && (
          <button
            type="button"
            onClick={() => unmarkTimedOutUser(msg.platform, msg.author.externalId)}
            aria-label={`Em timeout (${formatTimeoutRemaining(timeoutSecondsLeft)} restantes); clique para limpar a marca em ${who}`}
            title={`Timeout — ${formatTimeoutRemaining(timeoutSecondsLeft)} restantes. Clique para limpar.`}
            className="ml-1.5 inline-flex items-center justify-center text-2xs font-semibold uppercase tracking-wide px-1.5 min-w-8 h-8 rounded bg-warning/10 text-warning hover:bg-warning/20 transition-colors duration-fast cursor-pointer tabular-nums align-middle"
          >
            timed out · {formatTimeoutRemaining(timeoutSecondsLeft)}
          </button>
        )}
        <span className={`ml-1.5 text-fg${isDeleted ? ' line-through' : ''}`}>{msg.text}</span>
      </div>

      <div className="flex items-center gap-1.5 self-start font-mono text-2xs text-fg-subtle tabular-nums pt-0.5">
        {isLate && (
          <span className="opacity-0 group-hover:opacity-100 text-warning transition-opacity duration-fast">
            ⏱+{Math.round((Date.now() - msg.timestamp) / 1000)}s
          </span>
        )}
        {/* #101: timestamp hover-revealed por padrão (DESIGN-NOTES §3).
            Acessível a leitor de tela sempre (opacity, não display). */}
        <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-fast">
          {formatHHMM(msg.timestamp)}
        </span>
        <MsgActions
          msg={msg}
          onTimeout={onOpenTimeout}
          onDelete={onOpenDelete}
          onBan={onOpenBan}
        />
      </div>
    </div>
  );
}
